import { useConvexAuth, useConvexConnectionState, useMutation, useQuery } from "convex/react";
import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";

import { useBackendAvailability } from "../../app/backendAvailability";
import { useOnlineStatus } from "../../app/useOnlineStatus";
import { useAuthenticatedOwner } from "../../app/useAuthenticatedOwner";
import { backendApi } from "../../backend/api";
import { offlineDatabase } from "../../offline/database";
import { requestPersistentStorage } from "../../offline/storageQuota";
import {
  persistUpNextSnapshot,
  persistTrackingMutationResult,
  readCurrentOwner,
  rememberCurrentOwner,
} from "../../offline/trackingStore";
import { TrackingRuntimeContext, unavailableTrackingRuntime } from "./TrackingRuntimeContext";
import type { TrackingAvailability } from "./TrackingRuntimeContext";
import type { TrackingOperation } from "./types";

type StorageStatus = "opening" | "ready" | "blocked" | "error";

function ConnectedTrackingRuntime({ children }: PropsWithChildren) {
  const { isAuthenticated } = useConvexAuth();
  const connectionState = useConvexConnectionState();
  const isOnline = useOnlineStatus();
  const serverUpNext = useQuery(
    backendApi.tracking.listUpNextEntries,
    isAuthenticated ? {} : "skip",
  );
  const sendOperation = useMutation(backendApi.tracking.applyOperationV2);
  const [cachedOwnerId, setCachedOwnerId] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [storageStatus, setStorageStatus] = useState<StorageStatus>("opening");
  const mutationInFlightRef = useRef(false);
  /*
   * The account the token names, which is known before any query answers.
   *
   * The viewer query was the only source, so during a backend outage -- the
   * interface up, Convex unreachable -- there was no owner and every private
   * cache read came back empty: cached progress vanished and Up Next looked
   * empty for the whole outage, on a device holding all of it.
   */
  const authenticatedOwnerId = useAuthenticatedOwner();
  /*
   * The cached owner is a fallback for a browser that has not produced a token
   * yet. Where it used to stand in for an unresolved query, the token already
   * answers -- and answers with the account whose partition this is, so the
   * previous reader's snapshots cannot be picked up by whoever signs in next.
   *
   * Kept for the case the token cannot cover: a cold start offline, where the
   * auth client has nothing to hand back and a new account could not have
   * signed in without the network anyway.
   *
   * The original note, for why this is careful at all: a session that expires
   * or is revoked without `secureSignOut` leaves the
   * signed in.
   *
   * A session that expires or is revoked without `secureSignOut` leaves the
   * previous account's owner id and snapshots on the device. If someone else
   * signs in on the same browser, `viewer` is briefly unresolved -- and this
   * used to read the former account's tracking data under their session. Online
   * the query answers in a moment; offline it cannot answer at all, and a new
   * account cannot have signed in without the network.
   */
  const ownerId = authenticatedOwnerId ?? cachedOwnerId;

  useEffect(() => {
    let active = true;
    const onBlocked = () => {
      if (active) setStorageStatus("blocked");
    };
    offlineDatabase.on.blocked.subscribe(onBlocked);
    void offlineDatabase
      .open()
      .then(() => {
        if (active) setStorageStatus("ready");
      })
      .catch(() => {
        if (active) setStorageStatus("error");
      });
    return () => {
      active = false;
      offlineDatabase.on.blocked.unsubscribe(onBlocked);
    };
  }, []);

  useEffect(() => {
    /*
     * Asked once, when the database is actually usable. Without persistence a
     * browser under pressure may evict this origin between sessions, which
     * reads to someone as losing their data rather than as a cache miss.
     * Granted, denied or prompted are all fine and none are errors.
     */
    if (storageStatus === "ready") void requestPersistentStorage();
  }, [storageStatus]);

  useEffect(() => {
    let active = true;
    if (storageStatus === "ready") {
      void readCurrentOwner().then((value) => {
        if (active) setCachedOwnerId(value);
      });
    }
    return () => {
      active = false;
    };
  }, [storageStatus]);

  useEffect(() => {
    if (authenticatedOwnerId === null || storageStatus !== "ready") return;
    void rememberCurrentOwner(authenticatedOwnerId);
  }, [authenticatedOwnerId, storageStatus]);

  useEffect(() => {
    if (authenticatedOwnerId === null || serverUpNext === undefined || storageStatus !== "ready") {
      return;
    }
    void persistUpNextSnapshot(offlineDatabase, authenticatedOwnerId, serverUpNext);
  }, [authenticatedOwnerId, serverUpNext, storageStatus]);

  let availability: TrackingAvailability;
  if (storageStatus === "opening") availability = "storage-opening";
  else if (storageStatus === "blocked") availability = "storage-blocked";
  else if (storageStatus === "error") availability = "storage-error";
  else if (!isOnline) availability = "offline";
  else if (!isAuthenticated || authenticatedOwnerId === null) availability = "unavailable";
  else if (!connectionState.isWebSocketConnected) availability = "reconnecting";
  else availability = "ready";
  const canMutate = availability === "ready" && !isMutating;

  const mutate = useCallback(
    async (operation: TrackingOperation) => {
      if (
        authenticatedOwnerId === null ||
        storageStatus !== "ready" ||
        !isAuthenticated ||
        !isOnline ||
        !connectionState.isWebSocketConnected ||
        mutationInFlightRef.current
      ) {
        throw new Error("Tracking changes require a live authenticated connection.");
      }
      mutationInFlightRef.current = true;
      setIsMutating(true);
      const operationId = crypto.randomUUID();
      /*
       * `Promise.finally` rather than a `finally` block, which React Compiler
       * cannot lower -- one here costs this provider its automatic memoization.
       * The semantics are identical: the flags are released on both outcomes
       * and a rejection still propagates to the caller.
       */
      return await sendOperation({
        operationId,
        idempotencyKey: operationId,
        operation,
      })
        .then(async (result) => {
          await persistTrackingMutationResult(offlineDatabase, authenticatedOwnerId, result).catch(
            () => undefined,
          );
          return result;
        })
        .finally(() => {
          mutationInFlightRef.current = false;
          setIsMutating(false);
        });
    },
    [
      authenticatedOwnerId,
      connectionState.isWebSocketConnected,
      isAuthenticated,
      isOnline,
      sendOperation,
      storageStatus,
    ],
  );

  const runtimeValue = useMemo(
    () => ({ ownerId, availability, canMutate, isMutating, mutate }),
    [availability, canMutate, isMutating, mutate, ownerId],
  );

  return (
    <TrackingRuntimeContext.Provider value={runtimeValue}>
      {children}
    </TrackingRuntimeContext.Provider>
  );
}

export function TrackingRuntime({ children }: PropsWithChildren) {
  const backendAvailable = useBackendAvailability();
  return backendAvailable ? (
    <ConnectedTrackingRuntime>{children}</ConnectedTrackingRuntime>
  ) : (
    <TrackingRuntimeContext.Provider value={unavailableTrackingRuntime}>
      {children}
    </TrackingRuntimeContext.Provider>
  );
}
